<script setup lang="ts">
import {
	DialogClose,
	DialogContent,
	type DialogContentEmits,
	type DialogContentProps,
	DialogOverlay,
	DialogPortal,
	useEmitAsProps,
} from 'radix-vue'
import { X } from 'lucide-vue-next'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

interface SheetContentProps extends DialogContentProps {
	side?: 'left' | 'right' | 'top' | 'bottom'
	class?: string
	showCloseButton?: boolean
}

const props = defineProps<SheetContentProps>()

const emits = defineEmits<DialogContentEmits>()

const emitsAsProps = useEmitAsProps(emits)

const sheetVariants = cva(
	'gap-4 bg-background p-6 fixed z-50 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out',
	{
		variants: {
			side: {
				top: 'border-border inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
				bottom:
					'border-border inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
				left: 'border-border sm:max-w-sm inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
				right:
					'border-border sm:max-w-sm inset-y-0 right-0  h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
			},
		},
		defaultVariants: {
			side: 'right',
		},
	},
)
</script>

<template>
	<DialogPortal>
		<DialogOverlay
			class="fixed inset-0 z-50 bg-b-50% data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
		/>
		<DialogContent
			:class="cn(sheetVariants({ side: props.side }), props.class)"
			v-bind="{ ...props, ...emitsAsProps }"
		>
			<slot />

			<DialogClose
				v-if="props.showCloseButton !== false"
				class="top-4 right-4 p-0.5 hover:bg-secondary absolute rounded-md transition-colors"
			>
				<X class="w-4 h-4 text-muted-foreground" />
			</DialogClose>
		</DialogContent>
	</DialogPortal>
</template>
